import {
  attachmentsCreateSignedUrlCreateMutation,
  CreateTypeEnum,
  leadsImportCreateMutation,
  leadsImportListQueryKey,
  leadsImportPartialUpdateMutation,
  leadsImportRetrieveOptions,
  StepEnum
} from '@crm/api'
import { useImportLeads } from '@crm/features/leads/contexts'
import { useAppNavigate } from '@crm/hooks/useAppNavigate'
import { useIdParam } from '@crm/hooks/useIdParam'
import { paths } from '@crm/routes'
import { ACCEPT_TYPES_UPLOAD_FILE, onSubmitUploadDocument } from '@crm/utils'
import { Text } from '@shared/components'
import { useInvalidatePredicateQueries } from '@shared/hooks'
import { useLocales } from '@shared/locales'
import { showApiErrors } from '@shared/utils'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Button, Form, Radio, Space, Upload } from 'antd'
import { UploadChangeParam } from 'antd/es/upload'
import { UploadFile } from 'antd/lib'
import { RcFile } from 'antd/lib/upload'
import { Add } from 'iconsax-react'
import { useEffect, useState } from 'react'

const step = 'first'

export const UploadFileImport = () => {
  const { id } = useIdParam()
  const { t } = useLocales()
  const navigate = useAppNavigate()
  const [form] = Form.useForm()
  const [file, setFile] = useState<UploadFile | null>(null)
  const [attachmentId, setAttachmentId] = useState<number | null>(null)
  const { invalidateQueries } = useInvalidatePredicateQueries()
  const { setIsPending } = useImportLeads()

  const { data: leadImport } = useQuery({
    ...leadsImportRetrieveOptions({ path: { id: id } }),
    enabled: !!id
  })

  useEffect(() => {
    if (leadImport) {
      form.setFieldsValue({
        file: [
          {
            name: leadImport.attachment?.file,
            url: leadImport.attachment?.file
          }
        ],
        create_type: leadImport.create_type
      })
      setAttachmentId(leadImport.attachment?.id)
    }

    if (!leadImport) {
      form.setFieldValue('create_type', CreateTypeEnum.CREATE_RECORDS)
    }
  }, [leadImport])

  const { mutate: createAttachmentMutate } = useMutation({
    ...attachmentsCreateSignedUrlCreateMutation(),
    onSuccess: async (data) => {
      const signedUrl = data.upload_url
      const fileType = file?.type
      setAttachmentId(data.id)
      await onSubmitUploadDocument(signedUrl, file as RcFile, fileType)
    },
    onError: (error) => showApiErrors(error)
  })

  const { mutate: createMutation } = useMutation({
    ...leadsImportCreateMutation(),
    onMutate: () => setIsPending(true),
    onSuccess: (response) => {
      invalidateQueries([leadsImportListQueryKey()])
      navigate(
        paths.leadImport.item(
          response.id.toString(),
          StepEnum.CONFIRM_IMPORTED_FILE
        )
      )
    },
    onError: (error) => showApiErrors(error, form),
    onSettled: () => setIsPending(false)
  })

  const { mutate: updateMutation } = useMutation({
    ...leadsImportPartialUpdateMutation(),
    onMutate: () => setIsPending(true),
    onSuccess: (response) => {
      navigate(
        paths.leadImport.item(
          response.id.toString(),
          StepEnum.CONFIRM_IMPORTED_FILE
        )
      )
    },
    onError: (error) => showApiErrors(error),
    onSettled: () => setIsPending(false)
  })

  const onChange = ({
    file
  }: UploadChangeParam<UploadFile<{ url: string }>>) => {
    if (file.originFileObj) {
      const rcFile = file.originFileObj
      setFile(rcFile)

      createAttachmentMutate({
        body: { title: rcFile.name }
      })
    }
  }

  const onSubmit = () => {
    if (!attachmentId && !id) return

    const fileExtension = file?.name?.split('.').pop()?.toLowerCase()
    let fileType: 'csv' | 'excel' | undefined

    if (fileExtension === 'csv') fileType = 'csv'
    else if (fileExtension === 'xls' || fileExtension === 'xlsx')
      fileType = 'excel'

    const payload = {
      ...form.getFieldsValue(),
      attachment: attachmentId,
      type: fileType
    }

    if (!id) {
      createMutation({ body: payload })
    } else {
      updateMutation({ path: { id: id }, body: payload })
    }
  }

  const toAcceptBeforeUpload = (file: RcFile) => {
    if (ACCEPT_TYPES_UPLOAD_FILE.includes(file.type)) {
      return true
    }
    form.setFields([{ name: 'file', errors: [t('import.file_type')] }])
    return Upload.LIST_IGNORE
  }

  return (
    <>
      <Text weight={500} size={18}>
        {t('import.upload_first_step_description')}
      </Text>

      <Form
        layout="vertical"
        name={`form-${step}`}
        form={form}
        onFinish={onSubmit}
        className="mt-20"
      >
        <Form.Item
          name="file"
          valuePropName="fileList"
          label={t('import.file_type')}
          getValueFromEvent={(e) => e?.fileList}
          rules={[{ required: true, message: t('import.file_type') }]}
        >
          <Upload
            customRequest={({ onSuccess }) => onSuccess?.('')}
            onChange={onChange}
            beforeUpload={toAcceptBeforeUpload}
            maxCount={1}
            accept="text/csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
          >
            <Button type="primary" icon={<Add />}>
              {t('import.select_file')}
            </Button>
          </Upload>
        </Form.Item>

        <Form.Item name="create_type" label={t('import.data_process_type')}>
          <Radio.Group>
            <Space direction="vertical">
              <Radio value={CreateTypeEnum.CREATE_RECORDS}>
                {t('import.create_new')}
              </Radio>
              <Radio value={CreateTypeEnum.CREATE_UPDATE_RECORDS}>
                {t('import.create_new_and_update')}
              </Radio>
            </Space>
          </Radio.Group>
        </Form.Item>
      </Form>
    </>
  )
}
